import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useActiveCompany, pickActiveYear } from "../../context/ActiveCompanyContext";
import PageHeader from "../../components/PageHeader";
import EmptyState from "../../components/EmptyState";
import IndirectSummary from "../../components/planning/indirect/IndirectSummary";
import IndirectGrid from "../../components/planning/indirect/IndirectGrid";
import IndirectDetailPanel from "../../components/planning/indirect/IndirectDetailPanel";
import { g } from "../../components/planning/revenue/format";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

export default function IndirectCostsPage() {
  const { user, isAdmin } = useAuth();
  const canEdit = isAdmin || user?.role === "COMPANY_MANAGER";

  const [companies, setCompanies] = useState([]);
  const { companyId, setCompanyId, year, setYear } = useActiveCompany();
  const [years, setYears] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [panel, setPanel] = useState(null); // { accountId, month }
  const panelAccount = data?.accounts.find((a) => a.id === panel?.accountId) || null;

  useEffect(() => {
    api.get("/companies")
      .then((c) => {
        setCompanies(c.data);
        if (c.data.length > 0 && !c.data.some((co) => co.id === companyId)) setCompanyId(c.data[0].id);
      })
      .catch((e) => toast.error(apiError(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!companyId) return;
    setData(null);
    api
      .get(`/companies/${companyId}/periods/years`)
      .then((res) => {
        setYears(res.data);
        const pick = pickActiveYear(res.data, year);
        if (pick !== year) setYear(pick);
      })
      .catch((e) => toast.error(apiError(e)));
  }, [companyId, year]);

  const loadGrid = async () => {
    if (!companyId || !year) return;
    setLoading(true);
    try {
      const res = await api.get("/indirect-costs", { params: { company_id: companyId, year } });
      setData(res.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGrid();
  }, [companyId, year]); // eslint-disable-line react-hooks/exhaustive-deps

  const commitForecast = async (accountId, m, v) => {
    try {
      await api.put("/indirect-costs/forecast", {
        company_id: companyId,
        cost_account_id: accountId,
        year,
        month: m,
        forecast_amount: v,
      });
      // Targeted optimistic update — keeps the edited input mounted (Tab/Enter flow intact).
      setData((prev) => {
        if (!prev) return prev;
        const acct = prev.accounts.find((a) => a.id === accountId);
        if (!acct) return prev;
        const oldVal = g(acct.months, m)?.forecast_amount ?? 0;
        const newVal = v ?? 0;
        const delta = newVal - oldVal;
        const accounts = prev.accounts.map((a) =>
          a.id === accountId
            ? { ...a, months: { ...a.months, [m]: { ...g(a.months, m), forecast_amount: v, value: v, source: "INPUT" } }, fy: (a.fy ?? 0) + delta }
            : a
        );
        const md = g(prev.months, m);
        const months = {
          ...prev.months,
          [m]: { ...md, regular: { ...md.regular, forecast: md.regular.forecast + delta, value: md.regular.value + delta } },
        };
        const summary = {
          ...prev.summary,
          expected_fy_regular: prev.summary.expected_fy_regular + delta,
          forecast_remaining: prev.summary.forecast_remaining + delta,
        };
        return { ...prev, accounts, months, summary };
      });
      return true;
    } catch (e) {
      toast.error(apiError(e));
      return false;
    }
  };

  const applyForward = async (accountId, fromMonth, amount) => {
    try {
      const res = await api.put("/indirect-costs/forecast/apply-forward", {
        company_id: companyId,
        cost_account_id: accountId,
        year,
        from_month: fromMonth,
        forecast_amount: amount,
      });
      const skipped = res.data.skipped_closed.length;
      toast.success(
        `Applied to ${res.data.applied.length} month${res.data.applied.length === 1 ? "" : "s"}` +
          (skipped ? ` — ${skipped} closed month${skipped === 1 ? "" : "s"} skipped` : "")
      );
      await loadGrid();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const visibleAccounts = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.accounts.filter(
      (a) =>
        !q ||
        a.account_number.includes(q) ||
        (a.description || "").toLowerCase().includes(q)
    );
  }, [data, search]);

  return (
    <div data-testid="indirect-costs-page">
      <PageHeader
        title="Indirect Costs"
        subtitle="Monthly indirect operating cost forecast by cost account"
        actions={
          <div className="flex items-center gap-2">
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger className="w-56" data-testid="ic-company-select">
                <SelectValue placeholder="Select company" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {years.length > 0 && (
              <Select value={year ? String(year) : ""} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger className="w-28" data-testid="ic-year-select">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={String(y)} data-testid={`ic-year-${y}`}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        }
      />

      {companyId && years.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-lg" data-testid="ic-no-years">
          <EmptyState
            title="No financial years created"
            description="Create a financial year under Administration → Companies before planning."
            testId="ic-no-years-state"
          />
        </div>
      )}

      {year && data && <IndirectSummary summary={data.summary} />}

      {year && (
        <>
          <div className="flex items-center gap-2 mb-4">
            <Input
              className="w-72 bg-white"
              placeholder="Search account number or description…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="ic-search-input"
            />
          </div>
          <IndirectGrid
            data={data}
            loading={loading}
            visibleAccounts={visibleAccounts}
            canEdit={canEdit}
            onCommit={commitForecast}
            onOpenDetail={(accountId, month) => setPanel({ accountId, month })}
          />
        </>
      )}

      <IndirectDetailPanel
        open={!!panel}
        onClose={() => setPanel(null)}
        account={panelAccount}
        month={panel?.month || 1}
        data={data}
        year={year}
        canEdit={canEdit}
        onApplyForward={applyForward}
      />
    </div>
  );
}
